import { AnyError } from '@microsoft/portal-app/lib/IAnyError';
import { QueryStatus, useQuery } from 'react-query';
import { getAvailableLanguagesUrl } from '../../redux/modules/accountApis';
import { IAvailableLanguages, ILanguageEntry } from '../models';
import { QueryKey } from '../models/QueryKey';
import { useAuth } from '@iamexperiences/feature-auth';
import { useReportAPITelemetry } from './useReportAPITelemetry';
import { HttpRequestError } from '../../shared/HttpRequestError';
import { settingsManager } from '../../shared/settings';

interface IQueryResult {
  readonly status: QueryStatus;
  readonly availableDisplayLanguages: ILanguageEntry[];
  readonly availablePreferredLanguages: ILanguageEntry[];
  readonly availableTranslationLanguages: ILanguageEntry[] | null;
  readonly availableSpeechLanguages: ILanguageEntry[] | null;
}

const defaultLanguagePreference: IQueryResult = {
  status: 'idle',
  availableDisplayLanguages: [],
  availableSpeechLanguages: [],
  availablePreferredLanguages: [],
  availableTranslationLanguages: [],
};

export const useGetAvailableLanguagesQuery = (): IQueryResult => {
  const { auth } = useAuth();
  const { startTelemetryReport, stopTelemetryReport } = useReportAPITelemetry('getAvailableLanguages');

  const { data, status } = useQuery<IAvailableLanguages, AnyError>(QueryKey.AVAILABLE_LANGUAGES, async () => {
    startTelemetryReport({});
    const url = getAvailableLanguagesUrl();
    const clientId = settingsManager.getRequired('clientId');
    const token = await auth.acquireToken({ scopes: [clientId] });
    const response = await fetch(url, {
      headers: {
        Authorization: 'Bearer ' + token,
      },
    });

    const data = (await response.json()) as IAvailableLanguages;

    if (!response.ok) {
      const error = new HttpRequestError(response.status, response.statusText);
      stopTelemetryReport('error', error);
      throw error;
    }

    stopTelemetryReport('success', {});
    return data;
  });

  if (!data) {
    return defaultLanguagePreference;
  }

  const {
    availableDisplayLanguages,
    availablePreferredLanguages,
    availableSpeechLanguages,
    availableTranslationLanguages,
  } = data;

  return {
    status,
    availableDisplayLanguages,
    availablePreferredLanguages,
    availableSpeechLanguages,
    availableTranslationLanguages,
  };
};
